import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { FormGroup } from '@angular/forms';
import { addRemoveError, getControl, getGroup } from '@shared/helpers/form.helper';
import { toNum } from '@shared/helpers/math.helper';
import { INCONSISTENT_VALUE, REQUIRED_STATUS, VALUE_CANNOT_BE_SELECTED } from '@shared/constants/shared.constants';
import { formPaths } from '@shared/constants/form-paths.const';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { isEqual } from 'lodash-es';
import { contribLegge59CkHelper, getContribLegge59Control } from './helpers/contrib-legge-59-92-form.helpers';

export const contribLegge59BaseCalcoloRules: FormGroupRules = {
    my_Preaccantonamenti: (form: FormGroup) => {
        const my_PreaccantonamentiControl = getContribLegge59Control(form, 'my_Preaccantonamenti');
        const errors = my_PreaccantonamentiControl.errors ? my_PreaccantonamentiControl.errors : {};

        addRemoveError(isNilOrEmpty(my_PreaccantonamentiControl?.value), errors, 'my_Preaccantonamenti', {
            status: REQUIRED_STATUS,
        });

        addRemoveError(toNum(my_PreaccantonamentiControl?.value) < 0, errors, 'my_Preaccantonamenti', {
            status: INCONSISTENT_VALUE,
        });

        my_PreaccantonamentiControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_PreaccantonamentiAnno1: (form: FormGroup) => {
        const datiBilancioGroup = getGroup(form, formPaths.datiBilancio);
        const my_PreaccantonamentiAnno1Control = getContribLegge59Control(form, 'my_PreaccantonamentiAnno1');
        const my_DataApprovazione1: string = getControl(datiBilancioGroup, 'my_DataApprovazione1')?.value;
        const my_TipoSchemaBil1: string = getControl(datiBilancioGroup, 'my_TipoSchemaBil1')?.value;
        const my_BilancioAllegato: string = getControl(form, `${formPaths.bilancio}.my_BilancioAllegato`)?.value;
        const baseErrorCondition = !isNilOrEmpty(my_DataApprovazione1) && !isNilOrEmpty(my_TipoSchemaBil1) && !isNilOrEmpty(my_BilancioAllegato);
        const errors = my_PreaccantonamentiAnno1Control.errors ? my_PreaccantonamentiAnno1Control.errors : {};

        addRemoveError(baseErrorCondition && isNilOrEmpty(my_PreaccantonamentiAnno1Control?.value), errors, 'my_PreaccantonamentiAnno1', {
            status: REQUIRED_STATUS,
        });

        addRemoveError(baseErrorCondition && toNum(my_PreaccantonamentiAnno1Control?.value) < 0, errors, 'my_PreaccantonamentiAnno1', {
            status: INCONSISTENT_VALUE,
        });

        my_PreaccantonamentiAnno1Control.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_PreaccantonamentiInputatiIn: (form: FormGroup) => {
        const my_PreaccantonamentiInputatiInControl = getContribLegge59Control(form, 'my_PreaccantonamentiInputatiIn');
        const my_PreaccantonamentiInputatiIn: string = my_PreaccantonamentiInputatiInControl?.value;
        const my_Preaccontonamenti = toNum(getContribLegge59Control(form, 'my_Preaccantonamenti').value);
        const errors = my_PreaccantonamentiInputatiInControl.errors ? my_PreaccantonamentiInputatiInControl.errors : {};

        addRemoveError(!my_PreaccantonamentiInputatiIn?.includes('1') && toNum(my_Preaccontonamenti) !== 0, errors, 'my_PreaccantonamentiInputatiIn', {
            status: "Indicare l'imputazione nel conto Economico",
        });

        addRemoveError(my_PreaccantonamentiInputatiIn === 'E21', errors, 'my_PreaccantonamentiInputatiIn', {
            status: VALUE_CANNOT_BE_SELECTED,
        });

        my_PreaccantonamentiInputatiInControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_PreaccantonamentiInputatiInAnno1: (form: FormGroup) => {
        const datiBilancioGroup = getGroup(form, formPaths.datiBilancio);
        const my_PreaccantonamentiInputatiInAnno1Control = getContribLegge59Control(form, 'my_PreaccantonamentiInputatiInAnno1');
        const my_PreaccantonamentiInputatiInAnno1: string = my_PreaccantonamentiInputatiInAnno1Control?.value;
        const my_DataApprovazione1: string = getControl(datiBilancioGroup, 'my_DataApprovazione1')?.value;
        const my_TipoSchemaBil1: string = getControl(datiBilancioGroup, 'my_TipoSchemaBil1')?.value;
        const my_BilancioAllegato: string = getControl(form, `${formPaths.bilancio}.my_BilancioAllegato`)?.value;
        const my_PreaccontonamentiAnno1: string = getContribLegge59Control(form, 'my_PreaccantonamentiAnno1')?.value;
        const showImputazioneError =
            !my_PreaccantonamentiInputatiInAnno1?.includes('1') &&
            !isNilOrEmpty(my_DataApprovazione1) &&
            !isNilOrEmpty(my_TipoSchemaBil1) &&
            !isNilOrEmpty(my_BilancioAllegato) &&
            toNum(my_PreaccontonamentiAnno1) !== 0;
        const errors = my_PreaccantonamentiInputatiInAnno1Control.errors ? my_PreaccantonamentiInputatiInAnno1Control.errors : {};

        addRemoveError(showImputazioneError, errors, 'my_PreaccantonamentiInputatiInAnno1', {
            status: "Indicare l'imputazione nel conto Economico",
        });

        addRemoveError(my_PreaccantonamentiInputatiInAnno1 === 'E21', errors, 'my_PreaccantonamentiInputatiInAnno1', {
            status: VALUE_CANNOT_BE_SELECTED,
        });

        my_PreaccantonamentiInputatiInAnno1Control.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_Accantonamento: (form: FormGroup) => {
        const my_AccantonamentoControl = getContribLegge59Control(form, 'my_Accantonamento');
        const errors = my_AccantonamentoControl.errors ? my_AccantonamentoControl.errors : {};

        addRemoveError(isNilOrEmpty(my_AccantonamentoControl?.value), errors, 'my_Accantonamento', {
            status: REQUIRED_STATUS,
        });

        addRemoveError(toNum(my_AccantonamentoControl?.value) < 0, errors, 'my_Accantonamento', {
            status: INCONSISTENT_VALUE,
        });

        my_AccantonamentoControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_AccantonamentoAnno1: (form: FormGroup) => {
        const datiBilancioGroup = getGroup(form, formPaths.datiBilancio);
        const my_AccantonamentoAnno1Control = getContribLegge59Control(form, 'my_AccantonamentoAnno1');
        const my_AccantonamentoAnno1: string = my_AccantonamentoAnno1Control?.value;
        const my_DataApprovazione1: string = getControl(datiBilancioGroup, 'my_DataApprovazione1')?.value;
        const my_TipoSchemaBil1: string = getControl(datiBilancioGroup, 'my_TipoSchemaBil1')?.value;
        const my_BilancioAllegato: string = getControl(form, `${formPaths.bilancio}.my_BilancioAllegato`)?.value;
        const baseErrorCondition = !isNilOrEmpty(my_DataApprovazione1) && !isNilOrEmpty(my_TipoSchemaBil1) && !isNilOrEmpty(my_BilancioAllegato);
        const errors = my_AccantonamentoAnno1Control.errors ? my_AccantonamentoAnno1Control.errors : {};

        addRemoveError(baseErrorCondition && isNilOrEmpty(my_AccantonamentoAnno1), errors, 'my_AccantonamentoAnno1', {
            status: REQUIRED_STATUS,
        });

        addRemoveError(baseErrorCondition && toNum(my_AccantonamentoAnno1) < 0, errors, 'my_AccantonamentoAnno1', {
            status: INCONSISTENT_VALUE,
        });

        my_AccantonamentoAnno1Control.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_PerditeEserc: (form: FormGroup) => {
        const my_PerditeEsercControl = getContribLegge59Control(form, 'my_PerditeEserc');
        const my_PerditeEserc_calcolato = toNum(getContribLegge59Control(form, 'my_PerditeEserc_calcolato')?.value);
        const errors = my_PerditeEsercControl.errors ? my_PerditeEsercControl.errors : {};

        addRemoveError(isNilOrEmpty(my_PerditeEsercControl?.value), errors, 'my_PerditeEserc', {
            status: REQUIRED_STATUS,
        });

        addRemoveError(toNum(my_PerditeEsercControl?.value) < 0, errors, 'my_PerditeEserc', {
            status: INCONSISTENT_VALUE,
        });

        addRemoveError(toNum(my_PerditeEsercControl?.value) > my_PerditeEserc_calcolato, errors, 'my_PerditeEserc', {
            status: 'Valore eccessivo rispetto ai dati di bilancio',
        });

        my_PerditeEsercControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_PerditeEsercAnno1: (form: FormGroup) => {
        const datiBilancioGroup = getGroup(form, formPaths.datiBilancio);
        const my_PerditeEsercAnno1Control = getContribLegge59Control(form, 'my_PerditeEsercAnno1');
        const my_DataApprovazione1: string = getControl(datiBilancioGroup, 'my_DataApprovazione1')?.value;
        const my_TipoSchemaBil1: string = getControl(datiBilancioGroup, 'my_TipoSchemaBil1')?.value;
        const my_BilancioAllegato: string = getControl(form, `${formPaths.bilancio}.my_BilancioAllegato`)?.value;
        const my_PerditeEsercAnno1_calcolato = toNum(getContribLegge59Control(form, 'my_PerditeEsercAnno1_calcolato')?.value);
        const baseErrorCondition = !isNilOrEmpty(my_DataApprovazione1) && !isNilOrEmpty(my_TipoSchemaBil1) && !isNilOrEmpty(my_BilancioAllegato);
        const errors = my_PerditeEsercAnno1Control.errors ? my_PerditeEsercAnno1Control.errors : {};

        addRemoveError(baseErrorCondition && isNilOrEmpty(my_PerditeEsercAnno1Control?.value), errors, 'my_PerditeEsercAnno1', {
            status: REQUIRED_STATUS,
        });

        addRemoveError(baseErrorCondition && toNum(my_PerditeEsercAnno1Control?.value) < 0, errors, 'my_PerditeEsercAnno1', {
            status: INCONSISTENT_VALUE,
        });

        addRemoveError(toNum(my_PerditeEsercAnno1Control?.value) > my_PerditeEsercAnno1_calcolato, errors, 'my_PerditeEsercAnno1', {
            status: 'Valore eccessivo rispetto ai dati di bilancio',
        });

        my_PerditeEsercAnno1Control.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_EventRistorni: (form: FormGroup) => {
        const my_EventRistorniControl = getContribLegge59Control(form, 'my_EventRistorni');
        const my_CkApplicatiRistorni: string = getControl(form, `${formPaths.bilancio}.my_CkApplicatiRistorni`)?.value;
        const errors = my_EventRistorniControl.errors ? my_EventRistorniControl.errors : {};

        addRemoveError(toNum(my_EventRistorniControl?.value) < 0 && my_CkApplicatiRistorni === '1', errors, 'my_EventRistorni', {
            status: 'Il ristorno non può essere negativo',
        });

        my_EventRistorniControl.setErrors(isEqual(errors, {}) ? null : errors);

        contribLegge59CkHelper(
            form,
            'my_ContributoLegge59.my_EventRistorni',
            'my_Bilancio.my_CkApplicatiRistorni',
            1,
            'my_SezioneI__Rilevazione',
            true,
            REQUIRED_STATUS,
            'Il campo deve essere inserito obbligatoriamente, eventualmente inserire 0.'
        );
    },

    my_EventRistorniAnno1: (form: FormGroup) => {
        const datiBilancioGroup = getGroup(form, formPaths.datiBilancio);
        const bilancioGroup = getGroup(form, formPaths.bilancio);
        const my_EventRistorniAnno1Control = getContribLegge59Control(form, 'my_EventRistorniAnno1');
        const my_CkApplicaRistorniAnno1: string = getControl(bilancioGroup, 'my_CkApplicaRistorniAnno1')?.value;
        const my_DataApprovazione1: string = getControl(datiBilancioGroup, 'my_DataApprovazione1')?.value;
        const my_TipoSchemaBil1: string = getControl(datiBilancioGroup, 'my_TipoSchemaBil1')?.value;
        const my_BilancioAllegato: string = getControl(bilancioGroup, 'my_BilancioAllegato')?.value;
        const requiredError =
            isNilOrEmpty(my_EventRistorniAnno1Control?.value) &&
            my_CkApplicaRistorniAnno1 === '1' &&
            !isNilOrEmpty(my_DataApprovazione1) &&
            !isNilOrEmpty(my_TipoSchemaBil1) &&
            !isNilOrEmpty(my_BilancioAllegato);
        const errors = my_EventRistorniAnno1Control.errors ? my_EventRistorniAnno1Control.errors : {};

        addRemoveError(toNum(my_EventRistorniAnno1Control?.value) < 0 && my_CkApplicaRistorniAnno1 === '1', errors, 'my_EventRistorniAnno1', {
            status: 'Il ristorno non può essere negativo',
        });

        addRemoveError(requiredError, errors, 'my_EventRistorniAnno1', {
            message: 'Il campo deve essere inserito obbligatoriamente, eventualmente inserire 0.',
            status: REQUIRED_STATUS,
        });

        my_EventRistorniAnno1Control.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_EventAccantUtiRisInd: (form: FormGroup) => {
        eventAccantHelper(form, 'my_EventAccantUtiRisInd', 'my_BilAnno2', 'my_RisultEsercizio');
    },

    my_EventAccantUtiRisIndAnno1: (form: FormGroup) => {
        eventAccantHelper(form, 'my_EventAccantUtiRisIndAnno1', 'my_BilAnno1', 'my_RisultEsercizioAnno1');
    },
};

const eventAccantHelper = (form: FormGroup, targetPath: string, bilAnnoPath: string, risultEsercizioPath: string) => {
    const targetControl = getContribLegge59Control(form, targetPath);
    const targetValue: string = targetControl?.value;
    const bilAnno = toNum(getControl(form, `my_SezioneI__Rilevazione.my_DatiBilancio.${bilAnnoPath}`)?.value);
    const risultEsercizio = toNum(getContribLegge59Control(form, risultEsercizioPath)?.value);
    const percCondition = !isNilOrEmpty(targetValue) && toNum(targetValue) < risultEsercizio * 0.3;
    const baseCondition = bilAnno >= 2015 && risultEsercizio > 0;
    const errors = targetControl.errors ? targetControl.errors : {};

    addRemoveError(baseCondition && percCondition, errors, targetPath, {
        status: 'Attenzione: Inserire almeno il 30% degli utili',
    });

    addRemoveError(baseCondition && isNilOrEmpty(targetValue), errors, targetPath, {
        status: REQUIRED_STATUS,
    });

    targetControl.setErrors(isEqual(errors, {}) ? null : errors);
};
